import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { SCIENCE } from "@/lib/science-data";
import Footer from "@/components/numa/Footer";

const Nav = () => (
  <nav
    data-testid="science-nav"
    className="fixed top-0 left-0 right-0 z-50 bg-[#F9F9F6]/85 backdrop-blur-md border-b border-[#E2E5E0]"
  >
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
      <Link
        to="/"
        data-testid="science-nav-logo"
        className="font-heading text-[15px] tracking-[0.42em] font-medium text-[#152219]"
      >
        NUMA
      </Link>
      <div className="hidden md:flex items-center gap-10 text-[11px] tracking-[0.22em] uppercase text-[#57635A]">
        <span data-testid="science-nav-section">Science · NUMA-05</span>
      </div>
      <Link
        to="/"
        data-testid="science-nav-back"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#152219] hover:opacity-70 transition-opacity"
      >
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.4} />
        Protocol
      </Link>
    </div>
  </nav>
);

const Hero = () => (
  <section
    data-testid="science-hero"
    className="pt-28 sm:pt-32 pb-16 sm:pb-24 border-b border-[#E2E5E0]"
  >
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
      <div className="flex items-center justify-between text-[11px] tracking-[0.28em] uppercase text-[#8A9A90] mb-16">
        <span>The Science</span>
        <span className="hidden sm:inline">Vol. 01 / NUMA-05</span>
        <span>References</span>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="grid grid-cols-12 gap-6"
      >
        <div className="col-span-12 lg:col-span-8">
          <p className="text-[11px] tracking-[0.32em] uppercase text-[#3E5F4A] mb-8">
            Evidence Architecture
          </p>
          <h1
            className="font-heading text-5xl sm:text-6xl lg:text-[88px] leading-[0.98] tracking-[-0.03em] font-medium text-[#152219]"
            data-testid="science-title"
          >
            The studies behind
            <br />
            the protocol.
          </h1>
        </div>
        <div className="col-span-12 lg:col-span-4 lg:pl-8 self-end">
          <p className="text-[15px] leading-[1.7] text-[#57635A] max-w-md">
            NUMA-05 is built from compounds with peer-reviewed human or
            translational data. The references below are not exhaustive — they
            are the foundational citations that informed dose, format and
            inclusion.
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);

const ActiveBlock = ({ entry, index }) => {
  return (
    <motion.section
      id={entry.id}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="border-b border-[#E2E5E0] py-20 sm:py-28"
      data-testid={`science-active-${entry.id}`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header strip */}
        <div className="grid grid-cols-12 gap-6 mb-12 sm:mb-16">
          <div className="col-span-12 lg:col-span-8">
            <div className="flex items-center gap-4 mb-8">
              <span className="numa-marker">{entry.code}</span>
              <span className="h-[1px] w-12 bg-[#8A9A90]" />
              <span className="numa-marker text-[#152219]">{entry.role}</span>
            </div>
            <div className="flex items-baseline gap-6 flex-wrap">
              <h2
                className="font-heading text-4xl sm:text-5xl lg:text-[64px] tracking-[-0.025em] font-medium text-[#152219] leading-[1.02]"
                data-testid={`science-active-name-${entry.id}`}
              >
                {entry.name}
              </h2>
              <span
                className="font-heading text-2xl sm:text-3xl text-[#3E5F4A] tracking-tight font-mono-num"
                data-testid={`science-active-dose-${entry.id}`}
              >
                {entry.dose}
              </span>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:pl-8 self-end">
            <p className="text-[14.5px] leading-[1.7] text-[#152219]">
              {entry.thesis}
            </p>
          </div>
        </div>

        {/* Mechanism + studies grid */}
        <div className="grid grid-cols-12 gap-px bg-[#E2E5E0] border border-[#E2E5E0]">
          {/* Mechanism column */}
          <div className="col-span-12 lg:col-span-4 bg-white p-8 lg:p-10">
            <div className="numa-marker mb-8">Mechanism</div>
            <ol className="space-y-5 list-none">
              {entry.mechanism.map((m, i) => (
                <li
                  key={i}
                  className="flex gap-4 text-[13.5px] leading-[1.65] text-[#57635A]"
                  data-testid={`science-mechanism-${entry.id}-${i}`}
                >
                  <span className="font-heading text-[#8A9A90] font-mono-num shrink-0">
                    M.{String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{m}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Studies column */}
          <div className="col-span-12 lg:col-span-8 bg-white p-8 lg:p-10">
            <div className="numa-marker mb-8">Selected Studies</div>
            <ul className="space-y-px bg-[#E2E5E0] -mx-8 lg:-mx-10">
              {entry.studies.map((s, i) => (
                <li
                  key={s.doi}
                  className="bg-white px-8 lg:px-10 py-8"
                  data-testid={`science-study-${entry.id}-${i}`}
                >
                  <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-12 sm:col-span-9">
                      <h3 className="font-heading text-lg sm:text-xl tracking-tight text-[#152219] leading-snug">
                        {s.title}
                      </h3>
                      <p className="text-[12.5px] text-[#57635A] mt-2">
                        {s.authors} ·{" "}
                        <span className="italic">{s.journal}</span> · {s.year}
                      </p>
                      <p className="text-[13.5px] leading-[1.7] text-[#57635A] mt-4 max-w-2xl">
                        {s.finding}
                      </p>
                    </div>
                    <div className="col-span-12 sm:col-span-3 sm:text-right">
                      <a
                        href={`https://doi.org/${s.doi}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#3E5F4A] hover:text-[#152219] transition-colors"
                        data-testid={`science-doi-link-${entry.id}-${i}`}
                      >
                        LEARN
                        <ArrowUpRight
                          className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                          strokeWidth={1.4}
                        />
                      </a>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-[#8A9A90] mt-2 break-all">
                        {s.doi}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

const Disclaimer = () => (
  <section className="py-20 sm:py-24 bg-[#F9F9F6]" data-testid="science-disclaimer-section">
    <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
      <div className="numa-marker text-center mb-8">Notes</div>
      <p className="text-[13.5px] leading-[1.85] text-[#57635A] text-center">
        These references are provided for transparency. The cited literature
        describes mechanistic and clinical findings for individual compounds —
        not specific health claims about NR-05 or the NUMA protocol. Statements
        on this page have not been evaluated by any regulatory agency. NR-05
        is not intended to diagnose, treat, cure or prevent any disease.
      </p>
      <div className="mt-12 flex justify-center">
        <Link
          to="/#waitlist"
          data-testid="science-cta-waitlist"
          className="inline-flex items-center gap-3 bg-[#3E5F4A] hover:bg-[#2C4636] text-[#F9F9F6] px-8 py-4 text-[12px] tracking-[0.24em] uppercase transition-colors duration-500"
        >
          Enter the Protocol
          <ArrowUpRight className="w-4 h-4" strokeWidth={1.4} />
        </Link>
      </div>
    </div>
  </section>
);

export default function Science() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main
      data-testid="science-page"
      className="min-h-screen bg-[#F9F9F6] text-[#152219] font-body antialiased"
    >
      <Nav />
      <Hero />
      <div className="bg-white">
        {SCIENCE.map((entry, i) => (
          <ActiveBlock key={entry.id} entry={entry} index={i} />
        ))}
      </div>
      <Disclaimer />
      <Footer />
    </main>
  );
}
